/**
 * @name useSolicitacaoCredito
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Hook para gerenciar solicitações de crédito com validação cadastral e integração API
 * @package hooks
 */

import { useState } from 'react';
import useUserContext from '../contexts/UserContext/useUserContext';
import { cantinaApiClient } from '../services/cantina';
import { CadastroService } from '../services/supabase/entities/CadastroService';

interface DadosCredito {
     valor: number;
}

interface SolicitacaoCredito {
     valor_solicitacao: number;
     descricao_solicitacao: string;
     timestamp_limite_solicitacao: string;
}

interface DadosEndereco {
     cep: string;
     logradouro: string;
     bairro: string;
     cidade: string;
     uf: string;
     numero?: string;
     complemento?: string;
}

interface DadosTelefone {
     ddd: string;
     numero: string;
     tipo: 'principal' | 'secundario';
}

interface DadosCadastraisIncompletos {
     precisaModal: boolean;
     camposFaltantes: string[];
}

export function useSolicitacaoCredito(organizacaoId: number) {
     const [loading, setLoading] = useState(false);
     const [error, setError] = useState<string | null>(null);
     const [modalCadastral, setModalCadastral] = useState<DadosCadastraisIncompletos>({
          precisaModal: false,
          camposFaltantes: [],
     });
     const [pendingSolicitation, setPendingSolicitation] = useState<SolicitacaoCredito | null>(null);
     const { userData } = useUserContext();

     const validarDadosCadastrais = async (): Promise<DadosCadastraisIncompletos> => {
          if (!userData || !organizacaoId) {
               return { precisaModal: true, camposFaltantes: ['Usuário não autenticado'] };
          }

          const camposFaltantes: string[] = [];

          // Verificar endereço principal
          const { data: endereco } = await CadastroService.buscarEnderecoPrincipal(userData.id, organizacaoId);

          if (!endereco) {
               camposFaltantes.push('Endereço completo');
          }

          // Verificar telefone principal
          const { data: telefone } = await CadastroService.buscarTelefonePrincipal(userData.id, organizacaoId);

          if (!telefone) {
               camposFaltantes.push('Telefone de contato');
          }

          return {
               precisaModal: camposFaltantes.length > 0,
               camposFaltantes,
          };
     };

     const clearError = () => {
          setError(null);
     };

     const criarSolicitacao = async (dados: SolicitacaoCredito): Promise<string | null> => {
          if (!userData || !organizacaoId) {
               setError('Usuário não autenticado');
               return null;
          }

          // 🔐 VALIDAÇÕES DE SEGURANÇA NO FRONTEND

          // 1. Validação rigorosa de valor
          const valor = dados.valor_solicitacao;
          if (!valor || typeof valor !== 'number' || valor < 1 || valor > 1000) {
               setError(`Valor inválido. Deve estar entre R$ 1,00 e R$ 1.000,00. Valor informado: R$ ${valor}`);
               return null;
          }

          // 2. Sanitização de decimais
          const valorSeguro = Math.round(valor * 100) / 100;
          if (Math.abs(valorSeguro - valor) > 0.001) {
               setError(`Valor contém precisão decimal inválida: R$ ${valor}`);
               return null;
          }

          // 3. Validação de descrição
          if (!dados.descricao_solicitacao || dados.descricao_solicitacao.trim().length < 3) {
               setError('Descrição deve ter pelo menos 3 caracteres');
               return null;
          }

          // 4. Validação de timestamp
          const timestamp = new Date(dados.timestamp_limite_solicitacao);
          const agora = new Date();
          if (timestamp <= agora) {
               setError('Timestamp de expiração deve ser futuro');
               return null;
          }

          // 5. Atualizar dados com valores seguros
          const dadosSeguro: SolicitacaoCredito = {
               ...dados,
               valor_solicitacao: valorSeguro,
               descricao_solicitacao: dados.descricao_solicitacao.trim(),
          };

          setLoading(true);
          setError(null);
          setModalCadastral({ precisaModal: false, camposFaltantes: [] }); // Reset modal state

          try {
               // Validar dados cadastrais primeiro
               const validacao = await validarDadosCadastrais();
               if (validacao.precisaModal) {
                    setModalCadastral(validacao);
                    setPendingSolicitation(dadosSeguro); // Guardar dados seguros para tentar depois
                    setError(`Dados cadastrais incompletos: ${validacao.camposFaltantes.join(', ')}`);
                    return null; // Retorna null em vez de throw para não quebrar o fluxo
               }

               // Buscar dados cadastrais completos
               const { data: endereco, error: enderecoError } = await CadastroService.buscarEnderecoPrincipal(userData.id, organizacaoId);
               const { data: telefone, error: telefoneError } = await CadastroService.buscarTelefonePrincipal(userData.id, organizacaoId);

               // Verificar se houve erros nas consultas
               if (enderecoError || telefoneError) {
                    const erros = [];
                    if (enderecoError) erros.push('endereço');
                    if (telefoneError) erros.push('telefone');
                    throw new Error(`Erro ao buscar dados cadastrais: ${erros.join(', ')}`);
               }

               if (!endereco || !telefone) {
                    // Se não há dados, mostrar modal cadastral
                    const camposFaltantes = [];
                    if (!endereco) camposFaltantes.push('Endereço completo');
                    if (!telefone) camposFaltantes.push('Telefone de contato');

                    setModalCadastral({ precisaModal: true, camposFaltantes });
                    setPendingSolicitation(dadosSeguro); // Guardar dados seguros para tentar depois
                    setError(`Dados cadastrais não encontrados: ${camposFaltantes.join(', ')}`);
                    return null;
               }

               // Preparar payload para API usando dados seguros
               const solicitacaoPayload = {
                    fk_id_organizacao: organizacaoId,
                    geral: {
                         numeroConvenio: 125530, // Valor padrão configurado
                         timestampLimiteSolicitacao: dadosSeguro.timestamp_limite_solicitacao,
                         pagamentoUnico: true,
                         valorSolicitacao: dadosSeguro.valor_solicitacao, // 🔐 Usar valor seguro
                         codigoConciliacaoSolicitacao: `CNT-${Date.now()}-${userData.id}`,
                         descricaoSolicitacao: dadosSeguro.descricao_solicitacao, // 🔐 Usar descrição segura
                    },
                    devedor: {
                         tipoDocumento: userData.documento_tipo === 'CPF' ? 1 : 2, // 1 = CPF, 2 = CNPJ
                         numeroDocumento: parseInt(userData.documento_numero?.replace(/\D/g, '') || '0'),
                         cep: parseInt(endereco.cep?.replace(/\D/g, '') || '0'),
                         endereco: `${endereco.logradouro}${endereco.numero ? `, ${endereco.numero}` : ''}`,
                         bairro: endereco.bairro,
                         cidade: endereco.cidade,
                         uf: endereco.estado, // Usar 'estado' em vez de 'uf'
                         email: userData.email || undefined, // Converter null para undefined
                         dddTelefone: parseInt(telefone.ddd || '0'),
                         telefone: parseInt(telefone.numero?.replace(/\D/g, '') || '0'),
                    },
                    vencimento: {
                         data: dados.timestamp_limite_solicitacao.split('T')[0], // Apenas a data
                         multaValorFixo: 0,
                         multaPercentual: 0,
                         jurosPercentual: 0,
                    },
                    formasPagamento: [
                         {
                              codigoTipoPagamento: 'PIX' as const,
                              quantidadeParcelas: 1,
                         },
                    ],
                    repasse: {
                         tipoValorRepasse: 'Percentual' as const,
                         recebedores: [
                              {
                                   identificadorRecebedor: 125530,
                                   tipoRecebedor: 'Convenio' as const,
                                   valorRepasse: 100,
                              },
                         ],
                    },
               };

               console.log('🚀 Enviando solicitação para API:', solicitacaoPayload);

               // Chamar API de cantina
               const response = await cantinaApiClient.criarSolicitacaoCredito(solicitacaoPayload);

               if (response.success) {
                    console.log('✅ Solicitação criada com sucesso:', response.data);
                    return response.data.url_pagamento;
               } else {
                    throw new Error('Falha na criação da solicitação');
               }
          } catch (error) {
               console.error('❌ Erro ao criar solicitação:', error);
               const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido ao criar solicitação';
               setError(errorMessage);
               // Não fazer throw se é problema de dados cadastrais
               if (!errorMessage.includes('cadastrais incompletos') && !errorMessage.includes('cadastrais não encontrados')) {
                    throw new Error(errorMessage);
               }
               return null; // Retorna null em caso de dados cadastrais
          } finally {
               setLoading(false);
          }
     };

     const solicitarCredito = async (dados: DadosCredito) => {
          setLoading(true);
          try {
               // Validar dados cadastrais
               const validacao = await validarDadosCadastrais();

               if (validacao.precisaModal) {
                    setModalCadastral(validacao);
                    return { requiresCadastro: true };
               }

               // Se chegou aqui, dados estão completos - chamar API via criarSolicitacao
               console.log('Dados cadastrais completos, prosseguindo com solicitação:', dados);

               const urlSolicitacao = await criarSolicitacao({
                    valor_solicitacao: dados.valor,
                    descricao_solicitacao: `Recarga de R$ ${dados.valor.toFixed(2)}`,
                    timestamp_limite_solicitacao: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
               });

               return { success: true, data: { url_pagamento: urlSolicitacao } };
          } catch (error) {
               console.error('Erro ao solicitar crédito:', error);
               return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Erro desconhecido',
               };
          } finally {
               setLoading(false);
          }
     };

     const completarCadastro = async (dados: { endereco: DadosEndereco; telefone: DadosTelefone }) => {
          if (!userData || !organizacaoId) {
               throw new Error('Usuário não autenticado');
          }

          setLoading(true);
          try {
               const resultado = await CadastroService.atualizarDadosCompletos(userData.id, organizacaoId, dados);

               if (resultado.error) {
                    throw resultado.error;
               }

               setModalCadastral({ precisaModal: false, camposFaltantes: [] });
               return { success: true };
          } catch (error) {
               console.error('Erro ao completar cadastro:', error);
               throw error;
          } finally {
               setLoading(false);
          }
     };

     const fecharModalCadastral = () => {
          setModalCadastral({ precisaModal: false, camposFaltantes: [] });
     };

     const salvarDadosCadastrais = async (dados: { endereco?: DadosEndereco; telefone?: DadosTelefone }) => {
          if (!userData || !organizacaoId) {
               throw new Error('Usuário não autenticado');
          }

          setLoading(true);
          try {
               if (dados.endereco) {
                    await CadastroService.atualizarEnderecoPrincipal(userData.id, organizacaoId, dados.endereco);
               }

               if (dados.telefone) {
                    await CadastroService.atualizarTelefonePrincipal(userData.id, organizacaoId, dados.telefone);
               }

               setModalCadastral({ precisaModal: false, camposFaltantes: [] });
               setError(null);

               // Se havia uma solicitação pendente, tentar novamente
               if (pendingSolicitation) {
                    const resultado = await criarSolicitacao(pendingSolicitation);
                    setPendingSolicitation(null);
                    return resultado;
               }
          } catch (error) {
               console.error('Erro ao salvar dados cadastrais:', error);
               throw error;
          } finally {
               setLoading(false);
          }
     };

     return {
          loading,
          error,
          modalCadastral,
          solicitarCredito,
          criarSolicitacao,
          completarCadastro,
          fecharModalCadastral,
          salvarDadosCadastrais,
          clearError,
     };
}
